import { Box, InputAdornment, Typography, styled, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Theme } from '@mui/material/styles';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import StyledBackButton from '../Common/StyledBackButton';
import { chatSlimScrollbarStyles } from '../../utils/styles';
import { useAppSelector } from '../../redux/hooks';
import LoadingButton from '../Common/LoadingButton';
import StyledButton from '../Common/StyledButton';
import { BookingTypeEnum } from '../../redux/booking/bookingSlice';
import BookingTypeDropdown from './BookingTypeDropdown';
import { sessionTypes } from '../../types/booking';
import i18n from '../../i18n';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';

const StyledTextField = styled(TextField)<{ bordercolor?: string }>(
  ({
    theme,
    bordercolor = '#7CAEBE80',
    fontType = 'h6',
  }: {
    theme: Theme;
    bordercolor?: string;
    fontType?: keyof Theme['typography'];
  }) => ({
    '& .MuiInputBase-root': {
      backgroundColor: '#FFFFFF',
      borderRadius: '10px',
    },
    '& .MuiInputBase-input': {
      typography: theme.typography[fontType],
      color: '#2A3C67',
      ...chatSlimScrollbarStyles,
      '&:-webkit-autofill': {
        '-webkit-text-fill-color': 'theme.palette.primary.dark',
        '-webkit-box-shadow': `0 0 0 1000px ${theme.palette.primary.light} inset`,
      },
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        border: `1px solid ${bordercolor}`,
      },
      '&:hover fieldset': {
        border: '1px solid #7CAEBE',
      },
      '&.Mui-focused fieldset': {
        border: '1px solid #7CAEBE',
      },
    },
    '& ::placeholder': {
      opacity: 2,
    },
    '& .MuiFormHelperText-root': {
      margin: '0 !important',
      fontSize: 11,
    },
  })
);

export interface ICoachServicePackageCardProps {
  id?: string;
  coachId: string;
  title: string;
  rate: number;
  duration: number;
  description: string;
  activeStep?: number;
  onEdit?: () => void;
  onDelete?: () => void;
  bookingType: BookingTypeEnum;
  backgroundColor?: string;
  hasBookings?: boolean;
  setDeleteModePackageId?: (_id: string | null | undefined) => void;
  deleteModePackageId?: string | null;
  setEditModePackageId?: (_id: string | null) => void;
  editModePackageId?: string | null;
}

interface ICoachServicePackageEditCardProps {
  addPackage?: (_newPackage: ICoachServicePackageCardProps) => void;
  initialPackage?: ICoachServicePackageCardProps | null;
  updatePackage?: (_updatedPackage: ICoachServicePackageCardProps) => void;
  onDismiss?: () => void;
  backgroundColor?: string;
}

export default function CoachServicePackageEditCard({
  addPackage,
  initialPackage,
  updatePackage,
  onDismiss,
  backgroundColor,
}: Readonly<ICoachServicePackageEditCardProps>) {
  const coachId = useAppSelector((state) => state.coach.coach.id);
  const {
    createCoachServicePackagesLoading,
    addingNewPackageSuccess,
    updateCoachServicePackageLoading,
    updatingNewPackageSuccess,
  } = useAppSelector((state) => state.coach);
  const coachConfigurations = useAppSelector((state) => state.booking.coachConfigurations);
  const [showDropDown, setShowDropDown] = useState(false);
  const { t } = useTranslation();
  const validationSchema = Yup.object({
    title: Yup.string()
      .max(50, i18n.t('bookings.validations.title.max'))
      .required(i18n.t('bookings.validations.title.required')),
    bookingType: Yup.string().required(i18n.t('bookings.validations.bookingType.required')),
    rate: Yup.number()
      .required(i18n.t('bookings.validations.serviceFee.required'))
      .positive(i18n.t('bookings.validations.serviceFee.positive'))
      .typeError(i18n.t('bookings.validations.serviceFee.typeError')),
    duration: Yup.number()
      .required(i18n.t('bookings.validations.duration.required'))
      .positive(i18n.t('bookings.validations.duration.positive'))
      .typeError(i18n.t('bookings.validations.duration.typeError'))
      .max(240, i18n.t('bookings.validations.duration.max')),
    description: Yup.string()
      .max(300, i18n.t('bookings.validations.description.max'))
      .required(i18n.t('bookings.validations.description.required')),
  });

  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  const initialForm = {
    title: initialPackage?.title ?? '',
    bookingType: initialPackage?.bookingType ?? BookingTypeEnum.ONLINE,
    rate: initialPackage?.rate ?? 0,
    duration: initialPackage?.duration ?? 30,
    description: initialPackage?.description ?? '',
  };
  const formik = useFormik({
    initialValues: initialForm,
    validationSchema,
    onSubmit: (values) => {
      const packageData = {
        title: values.title,
        bookingType: values.bookingType,
        rate: parseFloat(values.rate.toString()),
        duration: parseInt(values.duration.toString(), 10),
        description: values.description,
        coachId,
      };
      if (initialPackage) {
        if (updatePackage) {
          updatePackage({ ...packageData, id: initialPackage.id });
        }
      } else if (addPackage) {
        addPackage(packageData);
      }
    },
  });

  useEffect(() => {
    if (addingNewPackageSuccess || updatingNewPackageSuccess) {
      formik.resetForm();
    }
  }, [addingNewPackageSuccess, updatingNewPackageSuccess]);

  useEffect(() => {
    if (
      coachConfigurations.isProvideOnlineConsultations &&
      coachConfigurations.isProvideInPersonConsultations
    ) {
      setShowDropDown(true);
    } else {
      setShowDropDown(false);
      if (coachConfigurations.isProvideOnlineConsultations) {
        formik.setFieldValue('bookingType', BookingTypeEnum.ONLINE);
      } else {
        formik.setFieldValue('bookingType', BookingTypeEnum.IN_PERSON);
      }
    }
  }, [
    coachConfigurations.isProvideOnlineConsultations,
    coachConfigurations.isProvideInPersonConsultations,
  ]);
  return (
    <Box
      sx={{
        minWidth: {
          xs: '70%',
          sm: '70%',
          md: 'calc(50% - 16px)',
          lg: '333px',
        },
        width: {
          xs: 'calc(50% - 16px)',
          sm: 'calc(50% - 16px)',
          md: 'calc(33.33% - 16px)',
          lg: 'calc(25% - 16px)',
        },
        height: '500px',
        borderRadius: '10px',
        border: '1px solid #7CAEBE',
        padding: '20px 20px 0px 20px',
        gap: '12px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        overflowX: 'hidden',
        overflowY: 'scroll',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(93,171,201,0.5)',
          borderRadius: '10px',
        },
        background: backgroundColor || 'transparent',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '48px',
            gap: '4px',
            display: 'flex',
            flexDirection: 'column',
            mb: formik.touched.title && formik.errors.title ? '12px' : '0',
          }}
        >
          <StyledTextField
            placeholder="Title"
            name="title"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.title}
            error={formik.touched.title && Boolean(formik.errors.title)}
            helperText={formik.touched.title && formik.errors.title}
            inputProps={{ maxLength: 50 }} // restricts the title to 50 characters
          />
        </Box>
        <BookingTypeDropdown
          name="bookingType"
          label="bookings.sessionType"
          options={sessionTypes}
          value={formik.values.bookingType || ''}
          placeholder="bookings.sessionType"
          isTranslationProvided
          onChange={formik.handleChange}
          error={formik.touched.bookingType ? formik.errors.bookingType : undefined}
          disabled={!showDropDown}
        />
        <Box
          sx={{
            width: '100%',
            maxHeight: '80px',
            display: 'flex',
            flexDirection: 'row',
            gap: '12px',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '76px',
              display: 'flex',
              flexDirection: 'column',
              mb: formik.touched.rate && formik.errors.rate ? '12px' : '0',
            }}
          >
            <Typography variant="subheadBold" fontSize={13} color="#2A3C67">
              {t('bookings.coachServicePackages.serviceFee')}
            </Typography>
            <StyledTextField
              placeholder=""
              name="rate"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ color: '#2A3C67' }}>
                    $
                  </InputAdornment>
                ),
              }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.rate}
              error={formik.touched.rate && Boolean(formik.errors.rate)}
              helperText={formik.touched.rate && formik.errors.rate}
            />
          </Box>
          <Box
            sx={{
              width: '100%',
              height: '76px',
              display: 'flex',
              flexDirection: 'column',
              mb: formik.touched.duration && formik.errors.duration ? '12px' : '0',
            }}
          >
            <Typography variant="subheadBold" fontSize={13} color="#2A3C67">
              {t('bookings.coachServicePackages.duration')}
            </Typography>
            <StyledTextField
              defaultValue={30}
              name="duration"
              InputProps={{
                endAdornment: <InputAdornment position="end">mins</InputAdornment>,
              }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.duration}
              error={formik.touched.duration && Boolean(formik.errors.duration)}
              helperText={formik.touched.duration && formik.errors.duration}
            />
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            height: '132px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="subheadBold" fontSize={13} color="#2A3C67">
            {t('bookings.coachServicePackages.description')}
          </Typography>
          <StyledTextField
            bordercolor="#7CAEBE80"
            rows={3}
            multiline
            name="description"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.description}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            placeholder={t('activity.createActivity.placeholder')}
            inputProps={{ maxLength: 300 }} // restricts the description to 300 characters
          />
        </Box>
      </Box>
      <Box
        sx={{
          height: '80px',
          borderTop: '1px solid #EEEEEE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '20px',
          width: '100%',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <StyledBackButton
            sx={{ width: '100%', paddingY: '9px !important' }}
            onClick={onDismiss}
            disabled={createCoachServicePackagesLoading || updateCoachServicePackageLoading}
          >
            <span>{t('bookings.coachServicePackages.dismiss')}</span>
          </StyledBackButton>
        </Box>
        <Box sx={{ flex: 1 }}>
          {createCoachServicePackagesLoading || updateCoachServicePackageLoading ? (
            <LoadingButton paddingY={12} fullWidth>
              {initialPackage
                ? t('bookings.coachServicePackages.update')
                : t('bookings.coachServicePackages.add')}
            </LoadingButton>
          ) : (
            <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
              <StyledButton
                fullWidth
                paddingY={12}
                disabled={
                  !checkPermissions(UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS)
                }
              >
                {initialPackage
                  ? t('bookings.coachServicePackages.update')
                  : t('bookings.coachServicePackages.add')}
              </StyledButton>
            </form>
          )}
        </Box>
      </Box>
    </Box>
  );
}
